import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ModelDoctorFilterInput } from "~/api/API";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { withErrorReporting } from "~/error-handling/reactQueryErrorUtil";
import { DoctorSearchParams, doctorService } from "~/services/doctor";

export const doctorKeys = {
  all: ["doctors"] as const,
  lists: (filter?: ModelDoctorFilterInput, isDemo?: boolean) =>
    [...doctorKeys.all, "list", filter, isDemo] as const,
  search: (params: DoctorSearchParams) =>
    [...doctorKeys.all, "search", params] as const,
  detail: (id: string) => [...doctorKeys.all, id] as const,
};

export interface DoctorListParams {
  filter?: ModelDoctorFilterInput;
}

export const useDoctor = (id?: string) => {
  return useQuery({
    queryKey: doctorKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) {
        return null;
      }

      return withErrorReporting(() => doctorService.getDoctor(id), {
        componentName: "DoctorDetail",
        operationName: "fetch-doctor-by-id",
        extra: { doctorId: id },
      });
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDoctors = (params: DoctorListParams = {}) => {
  const { userGroup } = useAuthSession();
  const { isDemo, loading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient();

  return useQuery({
    // isDemo is part of the key so that once the user record finishes loading
    // and isDemo flips from its default (false) to true, the query refetches
    // instead of reusing the cached non-demo list.
    queryKey: doctorKeys.lists(params.filter, isDemo),
    queryFn: async () => {
      if (!userGroup) {
        return [];
      }

      const result = await withErrorReporting(
        () => doctorService.fetchAllDoctors(userGroup, params.filter, isDemo),
        {
          componentName: "DoctorsList",
          operationName: "fetch-all-doctors",
          extra: {
            hasFilter: !!params.filter,
            isDemo,
          },
        },
      );

      // Set initial cache for each doctor, so the data is available immediately
      // when the user navigates to the doctor detail page, but mark as stale
      // to trigger background refetch.
      // Note: Marking as stale is necessary because the doctorList query is more shallow than the doctor detail query
      // so the cached doctor data from list may not contain all the data that should be displayed in the detail page
      result.forEach((doctor) => {
        queryClient.setQueryData(doctorKeys.detail(doctor.id), doctor);
        void queryClient.invalidateQueries({
          queryKey: doctorKeys.detail(doctor.id),
          exact: true,
        });
      });

      return result;
    },
    // Wait for the user record to load: isDemo defaults to false while loading,
    // so firing early would cache the non-demo list and not refetch.
    enabled: !!userGroup && !userLoading,
    // I think stale time for doctors list can be long because it does not change often
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

export const useDoctorSearch = (
  params: DoctorSearchParams,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: doctorKeys.search(params),
    queryFn: () => doctorService.searchDoctorsViaApi(params),
    enabled: enabled && !!params.query,
    staleTime: 30 * 1000,
  });
};
